/* eslint-disable complexity */

'use client'

import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  type ButtonProps,
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { Icon, Image } from '@media-components'
import { SectionWrapper, type SectionWrapperProps } from '@components/common'
import type { IconProps } from '@components/common/Icons'
import { GradientBackgroundWrapper } from '@components/entities'
import { useForm } from '@utils/form'
import { useReCaptcha } from '@utils/hooks/useReCaptcha'
import { type CaptchaResetRefType } from '@utils/hooks/useReCaptcha'
import { EmailPattern, publicImages } from '@utils/shared'
import { useSnackBar } from '@utils/snackbar'
import { CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StylesClassNames } from './styles'

export type NewsletterData = {
  title: string
  description?: string
}

export type NewsLetterSectionData = {
  newsLetterData: NewsletterData
}

export type NewsLetterSectionProps = NewsLetterSectionData & {
  csx?: CustomStyles<StylesClassNames>
  dark?: boolean
  buttonSize?: ButtonProps['size']
  iconSize?: IconProps['fontSize']
  companyLogo?: boolean
  showGlow?: boolean
  skipRecaptcha?: boolean
} & Partial<SectionWrapperProps>

export function NewsLetterSection({
  newsLetterData,
  csx,
  sx,
  disablePadding,
  dark = true,
  buttonSize = 'large',
  iconSize,
  showGlow,
  skipRecaptcha = false,
  alternateBg,
  companyLogo,
}: NewsLetterSectionProps) {
  const styles = getStyles(defaultStyles, csx)
  const [submitting, setSubmitting] = useState(false)
  const { show } = useSnackBar()
  const recaptchaRef = useRef<CaptchaResetRefType>(null)

  const {
    captchaToken,
    verificationError,
    isVerifying,
    verifyCaptcha,
    resetCaptcha,
    isTestMode,
  } = useReCaptcha()

  const { handleSubmit, register, isValid, reset } = useForm({
    initialValues: {
      email: '',
    },
    validationRules: {
      email: {
        required: 'Please enter your email address',
        pattern: {
          value: EmailPattern,
          message: 'Please enter a valid email address',
        },
      },
    },
  })
  const t = useTranslations('Newsletter')

  const resetForm = () => {
    reset()
    if (!skipRecaptcha) {
      // Reset the reCAPTCHA widget
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
      // Reset the captcha state
      resetCaptcha()
    }
  }

  const handleFormSubmit = async ({ email }: { email: string }) => {
    setSubmitting(true)

    try {
      if (!skipRecaptcha) {
        const isCaptchaVerified = await verifyCaptcha()
        if (!isCaptchaVerified && !isTestMode) {
          setSubmitting(false)
          return
        }
      }

      console.info('Submitting email:', email)

      await new Promise((resolve) => {
        setTimeout(resolve, 500)
      })

      show.success({
        title: 'Thank you for subscribing to our newsletter!',
      })

      resetForm()
    } catch (error) {
      console.error('Newsletter submission error:', error)
      show.error({
        title: 'An error occurred while subscribing',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const isFormValid = skipRecaptcha
    ? isValid
    : isValid && (captchaToken || isTestMode)
  const isSubmitDisabled =
    !isFormValid || submitting || (!skipRecaptcha && isVerifying)

  return (
    <SectionWrapper
      alternateBg={alternateBg}
      showGlow={showGlow}
      sx={sx}
      csx={{
        wrapper: styles('sectionWrapper').sx,
      }}
      disablePadding={disablePadding}
    >
      <GradientBackgroundWrapper {...styles('wrapper')}>
        <Box {...styles('contentWrapper')}>
          <Box {...styles('imageWrapper')}>
            {!companyLogo ? (
              <Icon
                iconName="Email"
                fontSize="medium"
                colorValues="neutral.white"
              />
            ) : (
              <Image
                alt="costimizer-logo2"
                src={publicImages.costimizerBlackLogo}
                fullWidth
              />
            )}
          </Box>
          {/* TODO: Add image wrapper */}

          <Typography
            variant="h5.500"
            color="common.white"
            {...styles('title')}
          >
            {newsLetterData.title}
          </Typography>
          <Box
            {...styles('infoWrapper')}
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit((data) => void handleFormSubmit(data))(e)
            }}
          >
            <Box {...styles('textFieldCaptchaWrapper')}>
              <TextField
                placeholder={t('enterEmail')}
                {...register('email')}
                variant="standard"
                {...styles(dark ? 'darkTextField' : 'textField')}
              />

              {/* v3 recaptcha runs invisibly via hook */}

              {verificationError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {verificationError}
                </Alert>
              )}
            </Box>

            <Button
              endIcon={
                <Icon
                  iconName="ArrowCircleRight"
                  colorValues="primary.contrastText"
                  fontSize={iconSize}
                />
              }
              size={buttonSize}
              color="primary"
              variant="contained"
              type="submit"
              {...styles('button')}
              disabled={isSubmitDisabled}
            >
              {submitting || isVerifying ? 'Subscribing...' : t('subscribe')}
            </Button>
          </Box>

          <Image
            alt="bo"
            {...styles('bgImage')}
            src="/images/bigoh-logo-cropped-2.svg"
          />
        </Box>
      </GradientBackgroundWrapper>
    </SectionWrapper>
  )
}
